import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { PageLayout, ActionButton, MultiLanguageTextInput } from "../../../components/modern";
import { adminApi } from "../../../utils/api";
import { showSuccessToast, showErrorToast } from "../../../utils/toastNotifications";

export const EditFaq: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation("faq");
  const isRTL = i18n.language === "ar";

  const [formData, setFormData] = useState({
    id: Number(id),
    categoryId: 0,
    question: "",
    answer: "",
    displayOrder: 0,
    isActive: true
  });

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [categories, setCategories] = useState<{ id: number; name: string }[]>([]);

  const getLocalizedText = (value?: string | null) => {
    if (!value) return "";

    try {
      if (value.startsWith("{")) {
        const parsed = JSON.parse(value);
        return isRTL ? parsed.ar || parsed.en || value : parsed.en || parsed.ar || value;
      }
    } catch {
      return value;
    }

    return value;
  };

  useEffect(() => {
    const fetchFaq = async () => {
      try {
        const response = await adminApi.call(`/faq/${id}`, "GET");

        if (!response.success) {
          showErrorToast(response.error || t("messages.fetchError"));
          return;
        }

        setFormData({
          id: response.data.id,
          categoryId: response.data.categoryId || 0,
          question: response.data.question || "",
          answer: response.data.answer || "",
          displayOrder: response.data.displayOrder || 0,
          isActive: response.data.isActive ?? true
        });
      } catch (error: any) {
        showErrorToast(t("messages.fetchError") + ": " + error.message);
      }
    };

    fetchFaq();
  }, [id]);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await adminApi.call("/faqcategory?page=1&pageSize=500", "GET");

        if (!response.success) {
          return;
        }

        setCategories(response.data?.items || []);
      } catch (error) {
        console.error("Error fetching FAQ categories:", error);
      }
    };

    fetchCategories();
  }, []);

  const validateForm = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.categoryId) {
      newErrors.categoryId = t("validation.categoryRequired");
    }

    if (!formData.question.trim()) {
      newErrors.question = t("validation.questionRequired");
    }

    if (!formData.answer.trim()) {
      newErrors.answer = t("validation.answerRequired");
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const submitUpdate = async () => {
    if (!validateForm()) {
      showErrorToast(t("validation.fillAllFields"));
      return;
    }

    setLoading(true);

    try {
      const response = await adminApi.call("/faq/admin/Update", "PUT", formData);

      if (!response.success) {
        showErrorToast(response.error || t("messages.updateError"));
        return;
      }

      showSuccessToast(t("messages.updateSuccess"));
    } catch (error: any) {
      showErrorToast(t("messages.updateError") + ": " + error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageLayout
      title={t("editFaq")}
      subtitle={t("subtitle")}
      breadcrumbs={[
        { label: t("breadcrumbs.faq"), path: "/faqs" },
        { label: t("breadcrumbs.edit") }
      ]}
      className={isRTL ? "rtl" : "ltr"}
    >
      <div className="card shadow-lg border-0 rounded-3 p-4">
        <div className="row g-3">
          <div className="col-md-6">
            <label className={`form-label ${isRTL ? "text-end" : "text-start"}`}>{t("fields.category")}</label>
            <select
              className={`form-select ${isRTL ? "text-end" : "text-start"} ${errors.categoryId ? "is-invalid" : ""}`}
              value={formData.categoryId}
              onChange={(e) => {
                setFormData((prev) => ({ ...prev, categoryId: Number(e.target.value) || 0 }));
                if (errors.categoryId) setErrors((prev) => ({ ...prev, categoryId: "" }));
              }}
              disabled={loading}
            >
              <option value={0}>{t("placeholders.category")}</option>
              {categories.map((category) => (
                <option key={category.id} value={category.id}>
                  {getLocalizedText(category.name)}
                </option>
              ))}
            </select>
            {errors.categoryId && <div className="invalid-feedback d-block">{errors.categoryId}</div>}
          </div>

          <div className="col-12">
            <MultiLanguageTextInput
              label={t("fields.question")}
              value={formData.question}
              onChange={(value) => {
                setFormData((prev) => ({ ...prev, question: value }));
                if (errors.question) setErrors((prev) => ({ ...prev, question: "" }));
              }}
              placeholder={t("placeholders.question")}
              error={errors.question}
              disabled={loading}
              required
              isRTL={isRTL}
            />
          </div>

          <div className="col-12">
            <MultiLanguageTextInput
              label={t("fields.answer")}
              value={formData.answer}
              onChange={(value) => {
                setFormData((prev) => ({ ...prev, answer: value }));
                if (errors.answer) setErrors((prev) => ({ ...prev, answer: "" }));
              }}
              placeholder={t("placeholders.answer")}
              error={errors.answer}
              disabled={loading}
              required
              isRTL={isRTL}
              multiline
              rows={5}
            />
          </div>

          <div className="col-md-6">
            <label className={`form-label ${isRTL ? "text-end" : "text-start"}`}>{t("fields.displayOrder")}</label>
            <input
              type="number"
              className={`form-control ${isRTL ? "text-end" : "text-start"}`}
              value={formData.displayOrder}
              onChange={(e) => setFormData((prev) => ({ ...prev, displayOrder: Number(e.target.value) || 0 }))}
            />
          </div>

          <div className="col-md-6">
            <label className={`form-label ${isRTL ? "text-end" : "text-start"}`}>{t("fields.isActive")}</label>
            <div className="form-check form-switch">
              <input
                type="checkbox"
                className="form-check-input"
                checked={formData.isActive}
                onChange={(e) => setFormData((prev) => ({ ...prev, isActive: e.target.checked }))}
              />
              <label className={`form-check-label ${isRTL ? "text-end" : "text-start"}`}>
                {formData.isActive ? t("status.active") : t("status.inactive")}
              </label>
            </div>
          </div>
        </div>

        <div className="text-center mt-4">
          <ActionButton variant="secondary" onClick={() => navigate("/faqs")} className={isRTL ? "ms-2" : "me-2"}>
            <i className={`fa ${isRTL ? "fa-arrow-right" : "fa-arrow-left"} ${isRTL ? "ms-1" : "me-1"}`}></i>
            {t("actions.back")}
          </ActionButton>
          <ActionButton variant="primary" onClick={submitUpdate} loading={loading} disabled={loading}>
            {loading ? t("actions.updating") : t("actions.update")}
          </ActionButton>
        </div>
      </div>
    </PageLayout>
  );
};
